/**
 * Admin — KCIS Make.com Operations (Sprint D)
 *
 * Mounts at /admin/kcis-make-operations. Super-admin only.
 *
 * Surfaces:
 *   - GET  /api/admin/kcis/make/health
 *   - GET  /api/admin/kcis/make/log
 *   - GET  /api/admin/kcis/make/deadletter
 *   - POST /api/admin/kcis/make/deadletter/{event_id}/replay
 *   - GET  /api/admin/kcis/make/events/summary
 *
 * Operational dashboard for the live Make.com webhook integration.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminAuth } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const EVENT_TYPES = [
  "", "assessment_started", "assessment_completed", "pathway_assigned",
  "cta_clicked", "funnel_confirmed", "escalation_flagged",
  "practitioner_review_recommended",
];
const STATUSES = ["", "delivered", "deadlettered", "permanent_failure", "stubbed"];

function Pill({ ok, label }) {
  return (
    <Badge variant={ok ? "default" : "outline"} className="text-[10px]">
      {label}
    </Badge>
  );
}

export default function AdminKcisMakeOperations() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [summary, setSummary] = useState([]);
  const [log, setLog] = useState({ items: [], total: 0 });
  const [dlq, setDlq] = useState({ items: [], total: 0 });
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [replayBusy, setReplayBusy] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin?.()) { navigate("/admin"); return; }
  }, [navigate]);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [h, s, l, d] = await Promise.all([
        api.get("/admin/kcis/make/health"),
        api.get("/admin/kcis/make/events/summary?since_days=7"),
        api.get(`/admin/kcis/make/log?page=1&page_size=50` +
                (eventTypeFilter ? `&event_type=${eventTypeFilter}` : "") +
                (statusFilter ? `&status=${statusFilter}` : "")),
        api.get("/admin/kcis/make/deadletter?open_only=true&page=1&page_size=25"),
      ]);
      setHealth(h.data); setSummary(s.data.summary || []);
      setLog(l.data); setDlq(d.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load Make ops"));
    } finally {
      setLoading(false);
    }
  }, [eventTypeFilter, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  async function replay(eventId) {
    setReplayBusy((b) => ({ ...b, [eventId]: true }));
    try {
      const r = await api.post(`/admin/kcis/make/deadletter/${eventId}/replay`);
      if (!r.data?.ok && !r.data?.skipped) {
        setError(formatApiError(r.data?.last_error || r.data?.status, "Replay failed"));
      }
      await refresh();
    } catch (e) {
      setError(formatApiError(e, "Replay error"));
    } finally {
      setReplayBusy((b) => ({ ...b, [eventId]: false }));
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopNav />
      <main className="flex-1 max-w-7xl mx-auto px-6 py-10 w-full" data-testid="admin-kcis-make-page">
        <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-purple-900 font-semibold">KCIS Adaptive — Sprint D</p>
            <h1 className="text-2xl sm:text-3xl font-semibold mt-1">Make.com Operations</h1>
            <p className="text-sm text-slate-600 mt-1">Webhook health, event delivery, deadletter & replay.</p>
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={refresh} data-testid="admin-kcis-make-refresh">Refresh</Button>
            <Button variant="outline" onClick={() => navigate("/admin/kcis-adaptive-reporting")} data-testid="admin-kcis-make-back-to-reporting">← Reporting</Button>
          </div>
        </header>

        {error && <p className="text-rose-700 text-sm mb-3" data-testid="admin-kcis-make-error">{error}</p>}
        {loading && !health && <p className="text-sm text-slate-500">Loading…</p>}

        {health && (
          <section className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="admin-kcis-make-health">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Webhook</p>
                <p className="mt-1"><Pill ok={health.enabled} label={health.enabled ? "Enabled" : "Disabled"} /></p>
                <p className="text-[11px] text-slate-500 mt-1">URL {health.base_url_set ? "configured" : "not set"}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Version</p>
                <p className="text-xl font-semibold mt-1">{health.config.webhook_version}</p>
                <p className="text-[11px] text-slate-500 mt-1">{health.config.signed ? "HMAC-signed" : "unsigned"}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Retry policy</p>
                <p className="text-sm mt-1 font-semibold">{health.config.max_attempts}× · {health.config.timeout_seconds}s</p>
                <p className="text-[11px] text-slate-500 mt-1">backoff {health.config.backoff_base_seconds}s exp</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Deadletter queue</p>
                <p className="text-xl font-semibold mt-1">{health.deadletter.open} <span className="text-sm text-slate-500">open</span></p>
                <p className="text-[11px] text-slate-500 mt-1">{health.deadletter.total} total</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5 text-center">
              {["delivered", "deadlettered", "permanent_failure", "stubbed"].map((k) => (
                <div key={k} className="rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid={`admin-kcis-make-24h-${k}`}>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">24h {k}</p>
                  <p className="text-xl font-semibold mt-1">{health.last_24h.counts?.[k] || 0}</p>
                </div>
              ))}
              <div className="rounded-lg border border-purple-200 p-3 bg-purple-50">
                <p className="text-[10px] uppercase tracking-widest text-purple-900">Latency p50/p95</p>
                <p className="text-xl font-semibold mt-1">{health.last_24h.p50_latency_ms}/{health.last_24h.p95_latency_ms}<span className="text-xs ml-1">ms</span></p>
              </div>
            </div>
          </section>
        )}

        {summary.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="admin-kcis-make-events-summary">
            <h3 className="text-xs uppercase tracking-widest text-purple-900 font-semibold mb-3">Event types · last 7 days</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Attempted</TableHead>
                  <TableHead>Delivered</TableHead>
                  <TableHead>Deadlettered</TableHead>
                  <TableHead>Stubbed</TableHead>
                  <TableHead>Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.map((row) => (
                  <TableRow key={row.event_type} data-testid="admin-kcis-make-events-summary-row">
                    <TableCell className="font-mono text-xs">{row.event_type}</TableCell>
                    <TableCell className="tabular-nums">{row.attempted}</TableCell>
                    <TableCell className="tabular-nums text-emerald-700">{row.delivered}</TableCell>
                    <TableCell className="tabular-nums text-rose-700">{row.deadlettered}</TableCell>
                    <TableCell className="tabular-nums text-slate-500">{row.stubbed}</TableCell>
                    <TableCell className="tabular-nums">{(row.delivery_rate * 100).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        )}

        <section className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="admin-kcis-make-deadletter">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-widest text-purple-900 font-semibold">Deadletter queue (open)</h3>
            <span className="text-xs text-slate-500">{dlq.total} open</span>
          </div>
          {dlq.items.length === 0 ? (
            <p className="text-sm text-slate-500">Queue is empty.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>At</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Last error</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dlq.items.map((it) => (
                  <TableRow key={it.event_id} data-testid="admin-kcis-make-deadletter-row">
                    <TableCell className="text-xs whitespace-nowrap">{new Date(it.at).toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-xs">{it.event_type}</TableCell>
                    <TableCell className="text-xs">{it.final_status}</TableCell>
                    <TableCell className="text-xs">{it.attempts?.length || 0}</TableCell>
                    <TableCell className="text-[11px] max-w-md truncate" title={it.last_error}>{it.last_error || "—"}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => replay(it.event_id)}
                        disabled={!!replayBusy[it.event_id]}
                        data-testid={`admin-kcis-make-deadletter-replay-${it.event_id}`}
                      >
                        {replayBusy[it.event_id] ? "Replaying…" : "Replay"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-kcis-make-log">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h3 className="text-xs uppercase tracking-widest text-purple-900 font-semibold">Recent webhook log</h3>
            <div className="flex gap-2 items-center">
              <select className="border rounded-md px-2 py-1 text-xs" value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)} data-testid="admin-kcis-make-log-filter-event">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t || "All events"}</option>)}
              </select>
              <select className="border rounded-md px-2 py-1 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-kcis-make-log-filter-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
              </select>
              <span className="text-xs text-slate-500">{log.total} total</span>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>At</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>HTTP</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Latency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {log.items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No log entries.</TableCell></TableRow>
              ) : log.items.map((it, i) => (
                <TableRow key={`${it.session_id}-${i}`} data-testid="admin-kcis-make-log-row">
                  <TableCell className="text-xs whitespace-nowrap">{new Date(it.at).toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-xs">{it.event_type}</TableCell>
                  <TableCell className="text-xs">
                    <Badge
                      variant={it.status === "delivered" ? "default" : (it.status === "deadlettered" || it.status === "permanent_failure") ? "destructive" : "outline"}
                      className="text-[10px]"
                    >
                      {it.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{it.status_code || "—"}</TableCell>
                  <TableCell className="text-xs">{it.attempt_count || 0}</TableCell>
                  <TableCell className="text-xs">{it.total_latency_ms || 0}ms</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      </main>
      <Footer />
    </div>
  );
}
